import { Dialog, DialogContent, DialogTitle, Input } from "@nook/ui"
import { Info } from "lucide-react"
import { useState } from "react"

import { isMac, Keys } from "@/components/keys.js"

/** Chrome's defaults from `wxt.config.ts`; the user may have rebound them. */
const EXT = isMac ? "Control+Shift" : "Alt+Shift"

const COLUMNS: [string, [string, string][]][][] = [
  [
    ["General", [
      ["Command palette", "$mod+k"],
      ["Search", "/"],
      ["New nook", "n"],
      ["New note", "Shift+n"],
      ["Ask Nook", "$mod+j"],
      ["Ask Nook from the palette", "$mod+Enter"],
      ["Show shortcuts", "[Shift]+?"],
      ["Collapse sidebar", "["],
    ]],
    ["Go to", [
      ["Library", "g l"],
      ["Ask Nook", "g a"],
      ["Settings", "g s"],
    ]],
  ],
  [
    ["Library", [
      ["Move down / up", "j k"],
      ["Move in the grid", "ArrowLeft ArrowUp ArrowDown ArrowRight"],
      ["Open nook", "Enter"],
      ["Open original", "o"],
      ["Delete", "$mod+Backspace"],
      ["Grid / list", "v"],
      ["Filter by source", "f"],
      ["Filter by date", "d"],
      ["Clear filters", "Shift+x"],
    ]],
    ["Open nook", [
      ["Next / previous", "j k"],
      ["Generate summary", "s"],
      ["Edit note", "e"],
      ["Larger / smaller text", "= -"],
      ["Expand / shrink", "w"],
      ["Highlight colour", "1 – 4"],
      ["Close", "Escape"],
    ]],
  ],
  [
    ["Ask", [
      ["Send", "Enter"],
      ["New line", "Shift+Enter"],
      ["New thread", "$mod+Shift+o"],
      ["Stop answer", "Escape"],
      ["Use example 1–3", "1 – 3"],
    ]],
    ["Chrome extension", [
      ["Nook the selection", `${EXT}+1`],
      ["Nook a screenshot", `${EXT}+2`],
      ["Nook this page", `${EXT}+3`],
      ["Open or close Nook", `${EXT}+j`],
    ]],
  ],
]

/** `?`: every shortcut, findable by name. */
export function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [find, setFind] = useState("")
  const matches = (label: string) => label.toLowerCase().includes(find.trim().toLowerCase())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} aria-describedby={undefined} className="gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[1040px]">
        <div className="flex items-center gap-4 border-b px-6 py-5">
          <DialogTitle className="flex-1 text-2xl font-normal">Keyboard shortcuts</DialogTitle>
          <Input
            aria-label="Find a shortcut"
            placeholder="Find a shortcut…"
            value={find}
            onChange={(event) => setFind(event.target.value)}
            className="h-8 w-60 text-[0.8125rem]"
          />
          <Keys keys="Escape" />
        </div>

        <div className="grid max-h-[min(70vh,calc(100dvh-11rem))] grid-cols-1 overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)] pb-4 md:grid-cols-3">
          {COLUMNS.map((sections, index) => (
            <div key={index} className="flex flex-col gap-6 p-6 md:border-r md:last:border-r-0">
              {sections.map(([name, rows]) =>
                rows.some(([label]) => matches(label)) ? (
                  <section key={name}>
                    <h3 className="text-muted-foreground pb-2 text-[0.6875rem] font-medium tracking-wide uppercase">{name}</h3>
                    {rows.filter(([label]) => matches(label)).map(([label, keys]) => (
                      <div key={label} className="flex h-8 items-center text-[0.8125rem]">
                        <span className="flex-1">{label}</span>
                        <Keys keys={keys} />
                      </div>
                    ))}
                  </section>
                ) : null,
              )}
            </div>
          ))}
        </div>

        <p className="bg-muted text-muted-foreground flex items-center gap-2 border-t px-6 py-3.5 text-xs">
          <Info className="size-3.5" />
          Single-key shortcuts are off while you type in a field. Extension shortcuts are set by Chrome at
          chrome://extensions/shortcuts.
        </p>
      </DialogContent>
    </Dialog>
  )
}
